import React, { useState, useEffect, useRef } from "react";
import { Modal, Tabs, Tooltip, Button, Form, Input } from "antd";
import {
  EyeOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  DeleteOutlined,
  DownloadOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
} from "@ant-design/icons";
import { Radio } from "antd";
import Loader from "../../utils/Spinners";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  getBasicDetails,
  getTimelineDetails,
  postDeficiencyQuestion,
} from "../../models/patientSlice";
import { errorMessage, successMessage } from "../../utils/notificationUtils";
import { IDocumentFields } from "../../interfaces/ICase";
import { fetchStepperData } from "../../models/preAuthSlice";
import { fetchQueries } from "../../models/preAuthQuerySlice";
import { SparkleIcon } from "../../utils/SparkleIcon";
import { errorMessageModal } from "../../utils/modalMessage";

const { TabPane } = Tabs;

type Document = {
  documentUrl: string;
  documentType: string;
  documentLabelType: string;
  documentName?: string;
  deficiencyCheckStatus?: any;
};

type DocumentPreviewModalProps = {
  open: boolean;
  handleCancel: () => void;
  documents: Document[];
  handleDeleteDocument: any;
  handleDownloadDocument: any;
  claimId?: any;
  handleDeficiencyModal?: any;
  deficiencyModal: boolean;
  deficiencyQuestions?: any;
  setDocumentName?: any;
  selectedDocument?: any;
  setUploadedDocumentUrl?: any;
  activeKey?: any;
  clickedActiveTab?: any;
  queryStageId?: any;
  isIntimated?: boolean;
};

const QueryUploadedDocument: React.FC<DocumentPreviewModalProps> = ({
  open,
  handleCancel,
  documents,
  handleDeleteDocument,
  handleDownloadDocument,
  claimId,
  handleDeficiencyModal,
  deficiencyModal,
  deficiencyQuestions,
  setDocumentName,
  selectedDocument,
  setUploadedDocumentUrl,
  activeKey,
  clickedActiveTab,
  queryStageId,
  isIntimated,
}) => {
  const [activeDocument, setActiveDocument] = useState<Document | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [documentKey, setDocumentKey] = useState<string>("");
  const [deficiencyStatus, setDeficiencyStatus] = useState<boolean>();
  const dispatch = useDispatch<AppDispatch>();
  const [showDocumentOnly, setShowDocumentOnly] = useState<boolean>(false);
  const { auth, patients, preAuth } = useSelector((state: RootState) => state);

  const currentVisitStage = preAuth?.stepperData?.visitStages?.find(
    (stage: any) => String(stage?.visitStageId) === String(activeKey),
  );
  const foLockedAfterQc =
    auth?.role?.role === "FO_STAFF" &&
    Boolean(isIntimated ?? currentVisitStage?.intimated);

  const handleShowDocument = () => {
    setShowDocumentOnly(!showDocumentOnly);
  };

  useEffect(() => {
    setShowDocumentOnly(false);
  }, [activeDocument]);

  useEffect(() => {
    setZoomLevel(1);
    setRotationAngle(0);
  }, [activeDocument]);

  const isLoading = !deficiencyQuestions;

  const [form] = Form.useForm();

  useEffect(() => {
    const initialValues: any = {};
    deficiencyQuestions?.documentsDeficiencyMappingList?.forEach(
      (question: any) => {
        initialValues[question?.documentsDeficiencyChecklistId] = [
          "true",
          true,
        ].includes(question?.status)
          ? "true"
          : ["false", false].includes(question?.status)
          ? "false"
          : null;
      }
    );
    initialValues["remarks"] = deficiencyQuestions?.remarks || "";
    form.setFieldsValue(initialValues);
  }, [deficiencyQuestions, form]);

  useEffect(() => {
    setDeficiencyStatus(deficiencyQuestions?.deficiencyCheckStatus);
  }, [deficiencyQuestions]);

  const onFinish = async (deficiencyStatus: any = false) => {
    try {
      await form.validateFields();

      const values = form.getFieldsValue();

      const updatedValues = {
        remarks: values?.remarks,
        stageId: activeKey ? parseInt(activeKey) : parseInt(queryStageId),
        documentId: selectedDocument?.id,
        documentDeficiencyStatus: deficiencyStatus,
        documentMasterId: selectedDocument?.documentType
          ? parseInt(selectedDocument?.documentType)
          : null,
        visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id || null,
        documentsDeficiencyMappingRequests: Object.keys(values)
          ?.filter((key) => key !== "remarks")
          ?.map((value) => {
            if (selectedDocument?.documentType) {
              return {
                documentId: selectedDocument?.id,
                documentMasterId: parseInt(selectedDocument?.documentType),
                documentsDeficiencyChecklistId: parseInt(value),
                status: values[value] === "true" ? true : false,
              };
            }
          }),
      };

      const res: any = await dispatch(postDeficiencyQuestion(updatedValues));

      if (res) {
        successMessage("Deficiency Check submitted Successfully");
        if (deficiencyStatus === false) {
          if (
            patients?.fetchBasicDetails?.casesResponse?.id &&
            patients?.fetchBasicDetails?.casesResponse?.portalMetaDataIds &&
            patients?.fetchBasicDetails?.visitInfoResponse?.id &&
            activeKey
          ) {
            dispatch(
              fetchQueries({
                caseId: patients?.fetchBasicDetails?.casesResponse?.id,
                visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
                portalMetaDataId:
                  patients?.fetchBasicDetails?.casesResponse
                    ?.portalMetaDataIds?.[0],
                stageId: [parseInt(activeKey)],
              })
            );

            dispatch(
              getTimelineDetails({
                caseId: patients?.fetchBasicDetails?.casesResponse?.id,
                visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
              })
            );

            dispatch(
              fetchStepperData({
                caseId: patients?.fetchBasicDetails?.casesResponse?.id,
                visitId: patients?.fetchBasicDetails?.visitInfoResponse?.id,
              })
            );

            if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
              dispatch(
                getBasicDetails({
                  id: patients?.fetchBasicDetails?.visitInfoResponse?.id,
                })
              );
            }
          }
        }
        form.resetFields();
        if (setUploadedDocumentUrl) {
          setUploadedDocumentUrl((prev: any) =>
            prev?.map((item: IDocumentFields) =>
              item?.id === selectedDocument?.id
                ? {
                    ...item,
                    deficiencyCheckStatus: res?.payload?.deficiencyCheckStatus,
                  }
                : item
            )
          );
        }

        handleCancel();
      }
    } catch (err: any) {
      if (err?.errorFields) {
        errorMessageModal("Please fill all details before submitting");
      } else {
        console.error(err);
        errorMessage(err?.response?.data || "Please, Try again after sometime");
        // form.resetFields();
      }
    }
  };

  const groupedDocuments: Record<string, Document[]> = documents?.reduce(
    (acc, doc) => {
      if (doc.documentUrl) {
        (acc[doc.documentLabelType] ||= []).push(doc);
      }
      return acc;
    },
    {} as Record<string, Document[]>
  );

  const [activeTab, setActiveTab] = useState<any>();

  useEffect(() => {
    if (clickedActiveTab) {
      setActiveTab(clickedActiveTab);
    } else if (!activeTab) {
      setActiveTab(Object.entries(groupedDocuments)?.[0]?.[0]);
    }
  }, [clickedActiveTab, open]);

  const isPdf = activeDocument?.documentUrl?.toLowerCase().endsWith(".pdf");

  const handleZoomIn = () => setZoomLevel((prev) => Math.min(prev + 0.1, 3));
  const handleZoomOut = () => setZoomLevel((prev) => Math.max(prev - 0.1, 0.5));
  const handleRotateLeft = () => setRotationAngle((prev) => prev - 90);
  const handleRotateRight = () => setRotationAngle((prev) => prev + 90);

  const initialSelectionRef = useRef(false);

  useEffect(() => {
    setActiveDocument(null);
  }, [activeTab]);

  useEffect(() => {
    if (
      open &&
      Object.keys(groupedDocuments).length > 0 &&
      activeTab &&
      !activeDocument
    ) {
      const doc = groupedDocuments?.[activeTab]?.[0];
      if (doc) {
        setActiveDocument(doc);
        doc?.documentName && setDocumentName(doc.documentName);
        doc?.documentType &&
          handleDeficiencyModal(
            doc.documentUrl,
            parseInt(doc.documentType),
            doc
          );
      }
    }
  }, [groupedDocuments, open, activeTab, activeDocument]);

  useEffect(() => {
    if (
      open &&
      !initialSelectionRef.current &&
      Object.keys(groupedDocuments).length > 0 &&
      activeTab
    ) {
      // const doc = Object.entries(groupedDocuments)[0][1][0];
      const doc = groupedDocuments?.[activeTab]?.[0];

      setActiveDocument(doc);

      if (doc?.documentName) {
        setDocumentName(doc?.documentName);
      }

      if (doc?.documentType) {
        handleDeficiencyModal(
          doc?.documentUrl,
          parseInt(doc?.documentType),
          doc
        );
      }

      initialSelectionRef.current = true;
    }
  }, [groupedDocuments, open, documentKey, activeTab]);

  useEffect(() => {
    if (!open) {
      initialSelectionRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (deficiencyQuestions) {
      const initialValues: any = {};
      deficiencyQuestions?.documentsDeficiencyMappingList?.forEach(
        (question: any) => {
          initialValues[question?.documentsDeficiencyChecklistId] = [
            "true",
            true,
          ].includes(question?.status)
            ? "true"
            : ["false", false].includes(question?.status)
            ? "false"
            : null;
        }
      );
      initialValues["remarks"] = deficiencyQuestions?.remarks || "";
      form.setFieldsValue(initialValues);

      setDeficiencyStatus(deficiencyQuestions?.deficiencyCheckStatus);
    }
  }, [deficiencyQuestions, form]);

  return (
    <div className="patient-details-stage-modal">
      <Modal
        open={open}
        onCancel={handleCancel}
        width={4000}
        style={{ top: 20 }}
        wrapClassName="patient-details-stage-modal"
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: isPdf ? "flex-end" : "space-between",
            }}
          >
            {!isPdf && activeDocument && (
              <div>
                <Button onClick={handleZoomOut} disabled={zoomLevel <= 0.5}>
                  <ZoomOutOutlined />
                </Button>
                <Button
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 3}
                  style={{ marginLeft: 8 }}
                >
                  <ZoomInOutlined />
                </Button>
                <Button onClick={handleRotateLeft} style={{ marginLeft: 8 }}>
                  <RotateLeftOutlined />
                </Button>
                <Button onClick={handleRotateRight} style={{ marginLeft: 8 }}>
                  <RotateRightOutlined />
                </Button>
              </div>
            )}
            {/* {activeDocument &&
              !showDocumentOnly &&
              auth?.role?.role !== "FO_STAFF" && (
                <div
                  className="footer-button"
                  style={{ display: "flex", gap: "0.4rem" }}
                >
                  {deficiencyQuestions?.deficiencyCheckStatus != null ? (
                    <></>
                  ) : (
                    deficiencyQuestions && (
                      <Button
                        type="primary"
                        htmlType="submit"
                        onClick={onFinish}
                      >
                        Save
                      </Button>
                    )
                  )}
                </div>
              )} */}
            <Button onClick={handleCancel}>Close</Button>
          </div>
        }
      >
        <Tabs
          type="card"
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
          }}
        >
          {groupedDocuments &&
            Object?.entries(groupedDocuments)?.map(([docType, docs]) => (
              <TabPane tab={docType} key={docType}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
                  {docs?.map((doc, index) => (
                    <div
                      key={index}
                      style={{
                        border:
                          activeDocument?.documentUrl === doc.documentUrl
                            ? "2px solid #005499"
                            : "1px solid #ccc",
                        borderRadius: "8px",
                        padding: "2px 8px",
                        //   background:
                        //     activeDocument?.documentUrl === doc.documentUrl
                        //       ? "#e6f7ff"
                        //       : "#fff",
                        width: 250,
                        display: "flex",
                        //   flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between",
                        background: doc?.deficiencyCheckStatus
                          ? "rgb(0, 109, 119)"
                          : doc?.deficiencyCheckStatus === "false" ||
                            doc?.deficiencyCheckStatus === false
                          ? "#DD2025"
                          : activeDocument?.documentUrl === doc?.documentUrl
                          ? "#e6f7ff"
                          : "#fff",
                        //   color: activeDocument?.deficiencyCheckStatus ? "white" : "black"
                      }}
                    >
                      <Tooltip
                        title={doc.documentLabelType || doc.documentName}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            color:
                              doc?.deficiencyCheckStatus ||
                              doc?.deficiencyCheckStatus === false
                                ? "white"
                                : "black",
                          }}
                        >
                          {(doc.documentLabelType || doc.documentName)?.slice(
                            0,
                            18
                          ) || `Document ${index + 1}`}
                        </div>
                      </Tooltip>
                      <div style={{ display: "flex", gap: 10 }}>
                        <Tooltip title="View">
                          <EyeOutlined
                            style={{
                              color:
                                doc?.deficiencyCheckStatus ||
                                doc?.deficiencyCheckStatus === false
                                  ? "white"
                                  : "#008B8B",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setActiveDocument(doc);
                              if (doc && doc?.documentName) {
                                setDocumentName(doc?.documentName);
                              }
                              if (doc?.documentType) {
                                handleDeficiencyModal(
                                  doc?.documentUrl,
                                  parseInt(doc?.documentType),
                                  doc
                                );
                              }
                            }}
                          />
                        </Tooltip>

                        <Tooltip title="Download">
                          <DownloadOutlined
                            style={{
                              color:
                                doc?.deficiencyCheckStatus ||
                                doc?.deficiencyCheckStatus === false
                                  ? "white"
                                  : "#008B8B",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              handleDownloadDocument(
                                doc.documentName,
                                doc.documentUrl
                              );
                            }}
                          />
                        </Tooltip>
                        {claimId || foLockedAfterQc ? (
                          <></>
                        ) : (
                          <Tooltip title="Delete">
                            <DeleteOutlined
                              style={{
                                color:
                                  doc?.deficiencyCheckStatus ||
                                  doc?.deficiencyCheckStatus === false
                                    ? "white"
                                    : "#ff4d4f",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                if (
                                  activeDocument?.documentUrl ===
                                  doc.documentUrl
                                ) {
                                  setActiveDocument(null);
                                }
                                handleDeleteDocument(
                                  doc.documentType,
                                  doc.documentUrl
                                );
                              }}
                            />
                          </Tooltip>
                        )}

                        {/* {doc?.deficiencyCheckStatus ? (
                        <Button
                          onClick={() => {
                            if (doc && doc?.documentName) {
                              setDocumentName(doc?.documentName);
                            }
                            if (doc?.documentType) {
                              handleDeficiencyModal(
                                doc?.documentUrl,
                                parseInt(doc?.documentType),
                                doc
                              );
                            }
                          }}
                          type="primary"
                          style={{ backgroundColor: "rgb(0, 109, 119)" }}
                        >
                          APPROVED
                        </Button>
                      ) : doc?.deficiencyCheckStatus === false ? (
                        <Button
                          onClick={() => {
                            if (doc && doc?.documentName) {
                              setDocumentName(doc?.documentName);
                            }
                            if (doc?.documentType) {
                              handleDeficiencyModal(
                                doc?.documentUrl,
                                parseInt(doc?.documentType),
                                doc
                              );
                            }
                          }}
                          type="primary"
                          danger
                        >
                          REJECTED
                        </Button>
                      ) : (
                        (Array?.isArray(deficiencyQuestions) ||
                          typeof deficiencyQuestions === "object") && (
                          // <Button
                          //   onClick={() => {
                          //     if (doc && doc?.documentName) {
                          //       setDocumentName(doc?.documentName);
                          //     }
                          //     if (doc?.documentType) {
                          //       handleDeficiencyModal(
                          //         doc?.documentUrl,
                          //         parseInt(doc?.documentType),
                          //         doc
                          //       );
                          //     }
                          //   }}
                          //   type="primary"
                          //   htmlType="submit"
                          // >
                          //   DEFICIENCY CHECK
                          // </Button>
                          <></>
                        )
                      )} */}
                      </div>
                    </div>
                  ))}
                </div>
              </TabPane>
            ))}
        </Tabs>

        {activeDocument && (
          <div
            className="headings"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "1rem",
            }}
          >
            <h3>{selectedDocument?.documentName}</h3>
            <h3
              style={{
                textDecoration: "underline",
                color: "red",
                cursor: "pointer",
              }}
              onClick={handleShowDocument}
            >
              {!showDocumentOnly
                ? "View Only Document"
                : "View Document Deficiency as well"}
            </h3>
          </div>
        )}

        {activeDocument &&
          (isPdf ? (
            <div
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
              key={documentKey}
            >
              <iframe
                key={documentKey}
                src={activeDocument.documentUrl}
                title="PDF Preview"
                width={!showDocumentOnly ? "58%" : "100%"}
                height="600px"
                // style={{ marginTop: 20 }}
              />
              <div
                className="content-deficiency-check"
                style={{
                  width: "42%",
                  overflow: "hidden",
                  paddingLeft: "0.5rem",
                  border: "0.4px solid #A6A6A6",
                  borderRadius: "16px",
                  padding: "0.6rem",
                  display: !showDocumentOnly ? "block" : "none",
                  transition: "all 0.3s",
                }}
              >
                {isLoading ? (
                  <Loader />
                ) : (
                  <>
                    <div
                      className="deficiency-options"
                      style={{ height: "550px", overflow: "auto" }}
                    >
                      <h4
                        style={{
                          textAlign: "center",
                          background: "#00549E",
                          color: "white",
                          padding: "10px",
                          borderTopLeftRadius: "16px",
                          borderTopRightRadius: "16px",
                        }}
                      >
                        Deficiency Check
                      </h4>
                      {/* <Form
                        name="deficiency-options"
                        autoComplete="off"
                        form={form}
                        labelAlign="left"
                        labelWrap
                        labelCol={{ span: 16 }}
                      >
                        {deficiencyQuestions &&
                          Array.isArray(
                            deficiencyQuestions?.documentsDeficiencyMappingList
                          ) &&
                          deficiencyQuestions?.documentsDeficiencyMappingList?.map(
                            (question: any) => (
                              <Form.Item
                                key={question?.documentsDeficiencyChecklistId}
                                label={`${question.question}`}
                                name={question?.documentsDeficiencyChecklistId}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please input answer!",
                                  },
                                ]}
                              >
                                <Radio.Group
                                  defaultValue={
                                    ["true", true].includes(question?.status)
                                      ? "true"
                                      : ["false", false].includes(
                                          question?.status
                                        )
                                      ? "false"
                                      : null
                                  }
                                  disabled={
                                    deficiencyQuestions?.deficiencyCheckStatus ==
                                    null
                                      ? false
                                      : true
                                  }
                                >
                                  <Radio value={"true"}> Yes </Radio>
                                  <Radio value={"false"}> No </Radio>
                                </Radio.Group>
                              </Form.Item>
                            )
                          )}
                        <Form.Item label="Remarks" name="remarks">
                          <Input
                            defaultValue={deficiencyQuestions?.remarks || ""}
                            placeholder={deficiencyQuestions?.remarks || ""}
                            disabled={
                              deficiencyQuestions?.deficiencyCheckStatus == null
                                ? false
                                : true
                            }
                          />
                        </Form.Item>
                      </Form> */}
                      <Form
                        name="deficiency-options"
                        autoComplete="off"
                        form={form}
                        labelAlign="left"
                        labelWrap
                        labelCol={{ span: 16 }}
                      >
                        {deficiencyQuestions?.documentsDeficiencyMappingList?.map(
                          (question: any, index: number) => {
                            const isEven = index % 2 === 0;
                            return (
                              <div
                                key={question?.documentsDeficiencyChecklistId}
                                className="question-wrapper"
                                style={{
                                  backgroundColor: isEven
                                    ? "#f0f0f0"
                                    : "#ffffff",
                                  padding: "6px 4px 0px 4px",
                                  borderBottom: "1px solid #d9d9d9",
                                }}
                              >
                                <Form.Item
                                  label={
                                    <span
                                      style={{
                                        fontWeight: 500,
                                        fontSize: "14px",
                                      }}
                                    >
                                      {question.question}
                                      {question?.reason && (
                                        <Tooltip
                                          placement="top"
                                          color="#fff"
                                          overlayInnerStyle={{
                                            color: "#333",
                                            background: "#fff",
                                            border: "1px solid #eee",
                                            fontSize: "14px",
                                          }}
                                          getPopupContainer={(triggerNode) =>
                                            triggerNode.parentElement as HTMLElement
                                          }
                                          title={question?.reason}
                                        >
                                          <span>
                                            <SparkleIcon />
                                          </span>
                                        </Tooltip>
                                      )}
                                    </span>
                                  }
                                  name={
                                    question?.documentsDeficiencyChecklistId
                                  }
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please input answer!",
                                    },
                                  ]}
                                >
                                  <Radio.Group
                                    buttonStyle="solid"
                                    defaultValue={
                                      ["true", true].includes(question?.status)
                                        ? "true"
                                        : ["false", false].includes(
                                            question?.status
                                          )
                                        ? "false"
                                        : null
                                    }
                                    disabled={
                                      (deficiencyQuestions?.deficiencyCheckStatus ==
                                      null
                                        ? false
                                        : true) ||
                                      auth?.role?.role === "FO_STAFF"
                                    }
                                  >
                                    <Radio.Button value="true">
                                      YES
                                    </Radio.Button>
                                    <Radio.Button value="false">
                                      NO
                                    </Radio.Button>
                                  </Radio.Group>
                                </Form.Item>
                              </div>
                            );
                          }
                        )}

                        <Form.Item
                          label="Remarks"
                          name="remarks"
                          // rules={[
                          //   {
                          //     required: true,
                          //     message: "Please input remarks!",
                          //   },
                          // ]}
                        >
                          <Input
                            defaultValue={deficiencyQuestions?.remarks || ""}
                            placeholder={deficiencyQuestions?.remarks || ""}
                            disabled={
                              (deficiencyQuestions?.deficiencyCheckStatus ==
                              null
                                ? false
                                : true) || auth?.role?.role === "FO_STAFF"
                            }
                          />
                        </Form.Item>
                      </Form>
                    </div>

                    {
                      // props?.deficiencyQuestions?.responseFromMl == true &&
                      deficiencyQuestions?.deficiencyCheckStatus == null &&
                      auth?.role?.role !== "FO_STAFF" ? (
                        <div
                          className="deficiency-buttons"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Button
                            block
                            type={deficiencyStatus ? "primary" : undefined}
                            style={{
                              border: "1px solid #008B8B",
                              color: !deficiencyStatus ? "#008B8B" : "white",
                            }}
                            htmlType="submit"
                            onClick={() => {
                              // setDeficiencyStatus(true)
                              onFinish(true);
                            }}
                          >
                            APPROVE
                          </Button>
                          <Button
                            block
                            type={
                              deficiencyStatus === false ? "primary" : undefined
                            }
                            danger
                            htmlType="submit"
                            // style={{ marginTop: "0.6rem" }}
                            onClick={() => {
                              // setDeficiencyStatus(false)
                              onFinish(false);
                            }}
                          >
                            REJECT
                          </Button>
                        </div>
                      ) : (
                        deficiencyQuestions?.deficiencyCheckStatus !== null && (
                          <div className="deficiency-buttons">
                            {["true", true].includes(
                              deficiencyQuestions?.deficiencyCheckStatus
                            ) ? (
                              <Button
                                block
                                type={deficiencyStatus ? "primary" : undefined}
                                style={{
                                  border: "1px solid #008B8B",
                                  color: "white",
                                  background: "#008B8B",
                                }}
                                htmlType="submit"
                              >
                                APPROVED
                              </Button>
                            ) : (
                              <Button
                                block
                                type={
                                  deficiencyStatus === false
                                    ? "primary"
                                    : undefined
                                }
                                danger
                                htmlType="submit"
                                style={{ marginTop: "0.6rem" }}
                              >
                                REJECTED
                              </Button>
                            )}
                          </div>
                        )
                      )
                    }
                  </>
                )}
              </div>
            </div>
          ) : (
            <div
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
              key={documentKey}
            >
              <div
                style={{
                  marginTop: 20,
                  display: "flex",
                  justifyContent: "center",
                  // alignItems: "center",
                  alignItems: "flex-start",
                  overflow: "auto",
                  height: 600,
                  width: !showDocumentOnly ? "58%" : "100%",
                }}
              >
                <div
                  style={{
                    // This wrapper ensures the scaled image has proper dimensions
                    minWidth: `${100 * zoomLevel}%`,
                    minHeight: `${100 * zoomLevel}%`,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <img
                    key={documentKey}
                    src={activeDocument.documentUrl}
                    alt="Preview"
                    style={{
                      transform: `scale(${zoomLevel}) rotate(${rotationAngle}deg)`,
                      transition: "transform 0.3s ease",
                      maxWidth: "none",
                      maxHeight: "none",
                      width: "100%",
                      height: "auto",
                    }}
                    // style={{
                    //   transform: `scale(${zoomLevel})`,
                    //   transition: "transform 0.3s ease",
                    //   maxWidth: "100%",
                    //   maxHeight: "100%",
                    //   width: "100%",
                    // }}
                  />
                </div>
              </div>
              <div
                className="content-deficiency-check"
                style={{
                  width: "42%",
                  overflow: "hidden",
                  paddingLeft: "0.5rem",
                  border: "0.4px solid #A6A6A6",
                  borderRadius: "16px",
                  padding: "0.6rem",
                  display: !showDocumentOnly ? "block" : "none",
                  transition: "all 0.3s",
                }}
              >
                {isLoading ? (
                  <Loader />
                ) : (
                  <>
                    <div
                      className="deficiency-options"
                      style={{ height: "550px", overflow: "auto" }}
                    >
                      <h4
                        style={{
                          textAlign: "center",
                          background: "#00549E",
                          color: "white",
                          padding: "10px",
                          borderTopLeftRadius: "16px",
                          borderTopRightRadius: "16px",
                        }}
                      >
                        Deficiency Check
                      </h4>
                      {/* <Form
                        name="deficiency-options"
                        autoComplete="off"
                        form={form}
                        labelAlign="left"
                        labelWrap
                        labelCol={{ span: 16 }}
                      >
                        {deficiencyQuestions &&
                          Array.isArray(
                            deficiencyQuestions?.documentsDeficiencyMappingList
                          ) &&
                          deficiencyQuestions?.documentsDeficiencyMappingList?.map(
                            (question: any) => (
                              <Form.Item
                                key={question?.documentsDeficiencyChecklistId}
                                label={`${question.question}`}
                                name={question?.documentsDeficiencyChecklistId}
                                rules={[
                                  {
                                    required: true,
                                    message: "Please input answer!",
                                  },
                                ]}
                              >
                                <Radio.Group
                                  defaultValue={
                                    ["true", true].includes(question?.status)
                                      ? "true"
                                      : ["false", false].includes(
                                          question?.status
                                        )
                                      ? "false"
                                      : null
                                  }
                                  disabled={
                                    deficiencyQuestions?.deficiencyCheckStatus ==
                                    null
                                      ? false
                                      : true
                                  }
                                >
                                  <Radio value={"true"}> Yes </Radio>
                                  <Radio value={"false"}> No </Radio>
                                </Radio.Group>
                              </Form.Item>
                            )
                          )}
                        <Form.Item label="Remarks" name="remarks">
                          <Input
                            defaultValue={deficiencyQuestions?.remarks || ""}
                            placeholder={deficiencyQuestions?.remarks || ""}
                            disabled={
                              deficiencyQuestions?.deficiencyCheckStatus == null
                                ? false
                                : true
                            }
                          />
                        </Form.Item>
                      </Form> */}
                      <Form
                        name="deficiency-options"
                        autoComplete="off"
                        form={form}
                        labelAlign="left"
                        labelWrap
                        labelCol={{ span: 16 }}
                      >
                        {deficiencyQuestions?.documentsDeficiencyMappingList?.map(
                          (question: any, index: number) => {
                            const isEven = index % 2 === 0;
                            return (
                              <div
                                key={question?.documentsDeficiencyChecklistId}
                                className="question-wrapper"
                                style={{
                                  backgroundColor: isEven
                                    ? "#f0f0f0"
                                    : "#ffffff",
                                  padding: "6px 4px 0px 4px",
                                  borderBottom: "1px solid #d9d9d9",
                                  marginBottom:
                                    index ===
                                    deficiencyQuestions
                                      ?.documentsDeficiencyMappingList?.length -
                                      1
                                      ? "1rem"
                                      : 0,
                                }}
                              >
                                <Form.Item
                                  label={
                                    <span
                                      style={{
                                        fontWeight: 500,
                                        fontSize: "14px",
                                      }}
                                    >
                                      {question.question}
                                      {question?.reason && (
                                        <Tooltip
                                          placement="top"
                                          color="#fff"
                                          overlayInnerStyle={{
                                            color: "#333",
                                            background: "#fff",
                                            border: "1px solid #eee",
                                            fontSize: "14px",
                                          }}
                                          getPopupContainer={(triggerNode) =>
                                            triggerNode.parentElement as HTMLElement
                                          }
                                          title={question?.reason}
                                        >
                                          <span>
                                            <SparkleIcon />
                                          </span>
                                        </Tooltip>
                                      )}
                                    </span>
                                  }
                                  name={
                                    question?.documentsDeficiencyChecklistId
                                  }
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please input answer!",
                                    },
                                  ]}
                                >
                                  <Radio.Group
                                    buttonStyle="solid"
                                    defaultValue={
                                      ["true", true].includes(question?.status)
                                        ? "true"
                                        : ["false", false].includes(
                                            question?.status
                                          )
                                        ? "false"
                                        : null
                                    }
                                    disabled={
                                      (deficiencyQuestions?.deficiencyCheckStatus ==
                                      null
                                        ? false
                                        : true) ||
                                      auth?.role?.role === "FO_STAFF"
                                    }
                                  >
                                    <Radio.Button value="true">
                                      YES
                                    </Radio.Button>
                                    <Radio.Button value="false">
                                      NO
                                    </Radio.Button>
                                  </Radio.Group>
                                </Form.Item>
                              </div>
                            );
                          }
                        )}

                        <Form.Item
                          label="Remarks"
                          name="remarks"
                          // rules={[
                          //   {
                          //     required: true,
                          //     message: "Please input remarks!",
                          //   },
                          // ]}
                          style={{ marginTop: "1rem" }}
                        >
                          <Input
                            defaultValue={deficiencyQuestions?.remarks || ""}
                            placeholder={deficiencyQuestions?.remarks || ""}
                            disabled={
                              (deficiencyQuestions?.deficiencyCheckStatus ==
                              null
                                ? false
                                : true) || auth?.role?.role === "FO_STAFF"
                            }
                          />
                        </Form.Item>
                      </Form>
                    </div>

                    {
                      // props?.deficiencyQuestions?.responseFromMl == true &&
                      deficiencyQuestions?.deficiencyCheckStatus == null &&
                      auth?.role?.role !== "FO_STAFF" ? (
                        <div
                          className="deficiency-buttons"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Button
                            block
                            type={deficiencyStatus ? "primary" : undefined}
                            style={{
                              border: "1px solid #008B8B",
                              color: !deficiencyStatus ? "#008B8B" : "white",
                            }}
                            htmlType="submit"
                            onClick={() => {
                              // setDeficiencyStatus(true)
                              onFinish(true);
                            }}
                          >
                            APPROVE
                          </Button>
                          <Button
                            block
                            type={
                              deficiencyStatus === false ? "primary" : undefined
                            }
                            danger
                            htmlType="submit"
                            // style={{ marginTop: "0.6rem" }}
                            onClick={() => {
                              // setDeficiencyStatus(false)
                              onFinish(false);
                            }}
                          >
                            REJECT
                          </Button>
                        </div>
                      ) : (
                        deficiencyQuestions?.deficiencyCheckStatus !== null && (
                          <div className="deficiency-buttons">
                            {["true", true].includes(
                              deficiencyQuestions?.deficiencyCheckStatus
                            ) ? (
                              <Button
                                block
                                type={deficiencyStatus ? "primary" : undefined}
                                style={{
                                  border: "1px solid #008B8B",
                                  color: "white",
                                  background: "#008B8B",
                                }}
                                htmlType="submit"
                              >
                                APPROVED
                              </Button>
                            ) : (
                              <Button
                                block
                                type={
                                  deficiencyStatus === false
                                    ? "primary"
                                    : undefined
                                }
                                danger
                                htmlType="submit"
                                style={{ marginTop: "0.6rem" }}
                              >
                                REJECTED
                              </Button>
                            )}
                          </div>
                        )
                      )
                    }
                  </>
                )}
              </div>
            </div>
          ))}
      </Modal>
    </div>
  );
};

export default QueryUploadedDocument;
